import { Suspense, lazy, type ReactNode } from 'react'
import { Link as RouterLink } from 'react-router-dom'
import Box from '@mui/material/Box'
import Alert from '@mui/material/Alert'
import Button from '@mui/material/Button'
import Card from '@mui/material/Card'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { useGetWebsiteTrafficQuery } from '../api/authoring'
import type { TrafficReport } from '../api/authoring'
import { isOperator } from '../auth/session'
import { operator } from '../model/routes'
import {
  caveat,
  changeText,
  dailyVisits,
  countText,
  dayText,
  headline,
  pageViewsText,
  type Headline,
  thisWeek,
  visitsText,
  weekCellText,
  weekDetail,
  weeksOf,
  weekStartText,
} from '../model/traffic'
import { WeeklyBars } from '../ui/WeeklyBars'

/**
 * The daily line, loaded when a card first has one to draw: the chart
 * library is the largest thing on the Overview, and a site that is not
 * connected never needs it.
 */
const LazySparkline = lazy(async () => ({ default: (await import('../ui/VisitsSparkline')).VisitsSparkline }))

/**
 * How often the site is visited, from the rows its host's numbers were
 * collected into (docs/plan/site-traffic.md §8).
 *
 * **A read that fails costs this card and nothing else**, the rule the
 * release reads on this screen already follow: the site's own document is
 * the one read the Overview cannot do without, and visitor numbers are
 * the furthest thing from it. A 503 — a build with no traffic store — is
 * the same sentence as any other failure, because to the reader it is
 * the same fact.
 *
 * **Every state says what it is rather than drawing nothing**, because
 * the admin shows everything: a site nobody has connected is told so and
 * told who can change it, and an operator, who can, is given the way
 * there.
 */
export function VisitsCard({ tenant, site }: { tenant: string; site: string }) {
  const traffic = useGetWebsiteTrafficQuery({ tenant, website: site })

  return (
    <Paper component="section" aria-labelledby="visits-heading">
      <Stack sx={{ px: 2, py: 1.5, borderBottom: 1, borderColor: 'divider' }}>
        <Typography variant="h3" id="visits-heading">
          Visits
        </Typography>
      </Stack>
      <Stack spacing={1.5} sx={{ px: 2, py: 2 }}>
        {traffic.isLoading ? (
          <Typography variant="body2" color="text.secondary">
            Reading visitor numbers…
          </Typography>
        ) : traffic.error || !traffic.data ? (
          <Alert severity="error">The visitor numbers could not be read. The rest of this page is not affected.</Alert>
        ) : (
          <Report tenant={tenant} report={traffic.data} />
        )}
      </Stack>
    </Paper>
  )
}

function Report({ tenant, report }: { tenant: string; report: TrafficReport }) {
  if (report.state === 'notConnected') {
    return (
      <Stack spacing={1} alignItems="flex-start">
        <Typography variant="body2" color="text.secondary">
          Visitor numbers are not connected for this site. Ask your agency to connect them.
        </Typography>
        {/* A courtesy, not a guard: the operator surface checks for
            itself, and this link only saves an operator the walk. */}
        {isOperator() && (
          <Button component={RouterLink} to={operator.tenantTraffic(tenant)} sx={{ ml: -1 }}>
            Connect
          </Button>
        )}
      </Stack>
    )
  }
  if (report.state === 'waiting') {
    return (
      <Typography variant="body2" color="text.secondary">
        Connected. The first numbers appear here once they have been collected.
      </Typography>
    )
  }

  const weeks = weeksOf(report)
  const lead = headline(weeks)
  const current = thisWeek(weeks)
  const label = lead.known
    ? `Visits by week. ${visitsText(lead.visits)} in the last four complete weeks.`
    : 'Visits by week. No numbers for the last four complete weeks.'
  const note = caveat(report)
  const daily = dailyVisits(report.days, weeks, report.through)

  return (
    <>
      {/* Both at once, side by side where there is room: the four weeks
          are the trend, and this week is what an author who has just
          published something comes to look at. */}
      <Stack direction="row" spacing={4} flexWrap="wrap" useFlexGap>
        <Figures figures={lead} none="No numbers for the last four complete weeks.">
          In the last four complete weeks
          {lead.change !== undefined && <>. {changeText(lead.change)}</>}.
        </Figures>
        <Figures figures={current} none="No numbers for this week yet.">
          {/* The week's figures stop where collection does, so the
              reader is told which day they run to. */}
          So far this week
          {report.through && report.through >= weeks[weeks.length - 1].start && <>, to {dayText(report.through)}</>}.
        </Figures>
      </Stack>
      {/* The bars and the line each framed, as two figures in the card
          rather than one run of drawing. Framed here and not by the
          components, which do not know what they are drawn inside. */}
      <Card sx={{ p: 2 }}>
        <WeeklyBars
          label={label}
          bars={weeks.map((week) => ({
            key: week.start,
            value: week.rows > 0 ? week.visits : undefined,
            partial: week.partial,
            incomplete: week.incomplete,
            detail: weekDetail(week),
          }))}
          table={{
            head: ['Week', 'Visits', 'Page views'],
            rows: weeks.map((week) => ({
              key: week.start,
              cells: [
                weekCellText(week),
                week.rows > 0 ? countText(week.visits) : '—',
                week.rows > 0 ? countText(week.pageViews) : '—',
              ],
            })),
          }}
          first={`Week of ${weekStartText(weeks[0])}`}
          last="This week"
        />
      </Card>
      {daily.length > 0 && (
        // The same height while it loads, so what is under it does not jump.
        <Card>
          <Suspense fallback={<Box sx={{ height: 250 }} />}>
            <LazySparkline points={daily} />
          </Suspense>
        </Card>
      )}
      {report.state === 'stale' && (
        <Alert severity="warning">
          {report.through ? `Last updated ${dayText(report.through)}.` : 'Visitor numbers are delayed.'}
        </Alert>
      )}
      {report.state === 'disconnected' && (
        <Alert severity="info">
          {report.through ? `No longer updated since ${dayText(report.through)}.` : 'No longer updated.'}
        </Alert>
      )}
      {note && (
        <Typography variant="caption" color="text.secondary">
          {note}
        </Typography>
      )}
    </>
  )
}

/** One headline: a count of visits, its page views and what they cover, or what is missing. */
function Figures({ figures, none, children }: { figures: Headline; none: string; children: ReactNode }) {
  if (!figures.known) {
    return (
      <Typography variant="body2" color="text.secondary">
        {none}
      </Typography>
    )
  }
  return (
    <Stack spacing={0.25}>
      <Stack direction="row" spacing={2} alignItems="baseline" flexWrap="wrap" useFlexGap>
        <Typography component="p" sx={{ fontSize: 28, lineHeight: 1.1, fontWeight: 600 }}>
          {countText(figures.visits)}{' '}
          <Typography component="span" variant="body2" color="text.secondary">
            {figures.visits === 1 ? 'visit' : 'visits'}
          </Typography>
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {pageViewsText(figures.pageViews)}
        </Typography>
      </Stack>
      <Typography variant="caption" color="text.secondary">
        {children}
      </Typography>
    </Stack>
  )
}
